import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles, Send, DollarSign, Users, FileText, Briefcase, AlertTriangle,
  Target, TrendingUp, TrendingDown, Trash2, Plus, Loader2, MessageSquare,
  Info, Mail,
} from "lucide-react";
import { toast } from "sonner";
import CoachSourcesDrawer from "@/components/CoachSourcesDrawer";
import CoachActionDrawer from "@/components/CoachActionDrawer";
import CoachDigestModal from "@/components/CoachDigestModal";
import CoachBulkDraftDrawer from "@/components/CoachBulkDraftDrawer";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "this_week", label: "This Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_30_days", label: "Last 30 Days" },
  { key: "quarter", label: "Quarter" },
];

const STARTERS = [
  { q: "What should I focus on today?", icon: Target },
  { q: "How is this month going?", icon: TrendingUp },
  { q: "Show me my biggest opportunities", icon: Sparkles },
  { q: "Which leads need attention?", icon: Users },
  { q: "Where am I losing revenue?", icon: TrendingDown },
  { q: "How do I hit my monthly goal?", icon: DollarSign },
];

const fmt$ = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const fmtPct = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n}%`);

export default function Coach() {
  const [period, setPeriod] = useState("this_month");
  const [snapshot, setSnapshot] = useState(null);
  const [snapLoading, setSnapLoading] = useState(true);

  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const [showSettings, setShowSettings] = useState(false);
  const [sourcesKind, setSourcesKind] = useState(null); // for Sources deep-link drawer
  const [actionInitial, setActionInitial] = useState(null); // for Action drawer
  const [showDigest, setShowDigest] = useState(false);
  const [bulkSignal, setBulkSignal] = useState(null);
  const nav = useNavigate();
  const navToLog = () => nav("/app/coach/approval-log");

  const loadSnapshot = async (p = period) => {
    setSnapLoading(true);
    try {
      const { data } = await api.get(`/business-coach/snapshot?period=${p}`);
      setSnapshot(data);
    } catch (e) {
      toast.error("Failed to load snapshot");
    } finally {
      setSnapLoading(false);
    }
  };

  const loadThreads = async () => {
    const { data } = await api.get("/business-coach/threads");
    setThreads(data);
  };

  const loadThread = async (id) => {
    setActiveThreadId(id);
    if (!id) { setMessages([]); return; }
    const { data } = await api.get(`/business-coach/threads/${id}`);
    setMessages(data.messages || []);
  };

  useEffect(() => { loadSnapshot(); loadThreads(); }, []);
  useEffect(() => { loadSnapshot(period); /* eslint-disable-next-line */ }, [period]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }); }, [messages, sending]);
  useEffect(() => {
    const h = (e) => setSourcesKind(e.detail?.kind);
    window.addEventListener("coach-open-sources", h);
    return () => window.removeEventListener("coach-open-sources", h);
  }, []);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q) return;
    setSending(true);
    setInput("");
    const provisional = [
      ...messages,
      { role: "user", content: q, created_at: new Date().toISOString() },
    ];
    setMessages(provisional);
    try {
      const { data } = await api.post("/business-coach/chat", {
        message: q, thread_id: activeThreadId, period,
      });
      setActiveThreadId(data.thread_id);
      setMessages([
        ...provisional,
        { role: "assistant", content: data.reply, sources: data.sources,
          created_at: new Date().toISOString() },
      ]);
      loadThreads();
    } catch (e) {
      toast.error("Coach failed to reply. Please try again.");
      setMessages(messages);
    } finally {
      setSending(false);
    }
  };

  const newThread = () => { setActiveThreadId(null); setMessages([]); };
  const deleteThread = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    await api.delete(`/business-coach/threads/${id}`);
    if (activeThreadId === id) { setActiveThreadId(null); setMessages([]); }
    loadThreads();
  };

  const rev = snapshot?.revenue || {};
  const leads = snapshot?.leads || {};
  const est = snapshot?.estimates || {};
  const jobs = snapshot?.jobs || {};
  const invc = snapshot?.invoices || {};
  const goals = snapshot?.goals || {};
  const priorities = snapshot?.priorities || [];
  const alerts = snapshot?.alerts || [];
  const missing = snapshot?.missing_data || [];

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="coach-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">
            <Sparkles className="w-3 h-3" /> AI Business Coach
          </div>
          <h1 className="font-heading text-4xl font-bold" data-testid="coach-title">
            Your business. Your numbers. Your next move.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white border border-zinc-200 rounded-lg p-1" data-testid="coach-period-selector">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`coach-period-${p.key}`}
                className={`px-3 py-1.5 text-xs rounded-md transition ${
                  period === p.key
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-600 hover:bg-zinc-100"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => setBulkSignal("stale_estimates")}
            data-testid="coach-bulk-drafts-btn"
          >
            Bulk drafts
          </Button>
          <Button
            variant="outline"
            onClick={navToLog}
            data-testid="coach-approval-log-btn"
          >
            Approval log
          </Button>
          <Button
            variant="outline"
            onClick={() => nav("/app/coach/corrections")}
            data-testid="coach-corrections-btn"
          >
            Corrections
          </Button>
          <Button
            variant="outline"
            onClick={() => nav("/app/coach/rules")}
            data-testid="coach-rules-btn"
          >
            Rules Lab
          </Button>
          <Button
            variant="outline"
            onClick={() => nav("/app/coach/verification")}
            data-testid="coach-verification-btn"
          >
            Verification
          </Button>
          <Button
            variant="outline"
            onClick={() => nav("/app/coach/import-preflight")}
            data-testid="coach-import-preflight-btn"
          >
            Import preflight
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowDigest(true)}
            data-testid="coach-digest-btn"
          >
            <Mail className="w-4 h-4 mr-1"/> Weekly digest
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowSettings(true)}
            data-testid="coach-settings-btn"
          >
            <Target className="w-4 h-4 mr-1" /> Goals
          </Button>
        </div>
      </div>

      {/* Snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="coach-snapshot-grid">
        <Tile icon={DollarSign} label="Revenue (paid)" value={fmt$(rev.native_paid_current_period)}
              sub={fmtPct(rev.delta_percent)} accent tone={rev.delta_percent >= 0 ? "up" : "down"}
              testid="coach-tile-revenue"
              onClick={() => setSourcesKind("invoices")} />
        <Tile icon={DollarSign} label="Outstanding" value={fmt$(rev.outstanding_total)}
              sub={rev.outstanding_overdue > 0 ? `${fmt$(rev.outstanding_overdue)} overdue` : "None overdue"}
              testid="coach-tile-outstanding"
              onClick={() => setSourcesKind(rev.outstanding_overdue > 0 ? "overdue_invoices" : "invoices")} />
        <Tile icon={Users} label="New leads" value={leads.new_current_period ?? "—"}
              sub={fmtPct(leads.delta_percent)} testid="coach-tile-leads"
              onClick={() => setSourcesKind("leads")} />
        <Tile icon={FileText} label="Estimate close rate"
              value={est.close_rate_percent != null ? `${est.close_rate_percent}%` : "—"}
              sub={est.close_rate_percent == null ? "Not enough decisions" : `${est.accepted_current_period}/${(est.accepted_current_period||0)+(est.declined_current_period||0)} accepted`}
              testid="coach-tile-close-rate"
              onClick={() => setSourcesKind("estimates")} />
        <Tile icon={Briefcase} label="Jobs completed" value={jobs.completed_current_period ?? "—"}
              sub={fmt$(jobs.completed_revenue_current_period)} testid="coach-tile-jobs"
              onClick={() => setSourcesKind("jobs")} />
        <Tile icon={FileText} label="Estimates sent" value={est.sent_current_period ?? "—"}
              testid="coach-tile-estimates-sent"
              onClick={() => setSourcesKind("estimates")} />
        <Tile icon={DollarSign} label="Average invoice" value={fmt$(rev.average_invoice)}
              sub={rev.average_invoice == null ? "Insufficient data" : null}
              testid="coach-tile-avg-invoice"
              onClick={() => setSourcesKind("invoices")} />
        <Tile icon={Target} label="Pipeline value" value={fmt$(snapshot?.pipeline?.total_value)}
              testid="coach-tile-pipeline"
              onClick={() => setSourcesKind("leads")} />
      </div>

      {/* Goal pacing */}
      {goals?.monthly_revenue && (
        <Card className="p-5 mb-6 border-orange-100 bg-orange-50/30" data-testid="coach-goal-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-mono-accent uppercase tracking-wider text-[#FF5A00] mb-1">Monthly goal pacing</div>
              <div className="font-heading text-2xl font-bold">
                {fmt$(goals.monthly_revenue.actual)} / {fmt$(goals.monthly_revenue.goal)}
                <span className="text-base text-zinc-500 font-normal ml-2">
                  ({goals.monthly_revenue.percent_complete}%)
                </span>
              </div>
              <div className="text-sm text-zinc-600 mt-1">
                {fmt$(goals.monthly_revenue.remaining)} remaining · {goals.monthly_revenue.days_remaining_this_month} days left · need <b>{fmt$(goals.monthly_revenue.required_daily_pace)}/day</b>
              </div>
            </div>
            <div className={`text-xs px-2 py-1 rounded-full ${
              goals.monthly_revenue.on_track ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
            }`}>
              {goals.monthly_revenue.on_track ? "On track" : "Behind pace"}
            </div>
          </div>
          <div className="mt-3 h-2 bg-white rounded-full overflow-hidden">
            <div className="h-full bg-[#FF5A00]"
                 style={{ width: `${Math.min(100, goals.monthly_revenue.percent_complete || 0)}%` }} />
          </div>
        </Card>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="coach-alerts">
          {alerts.map((a, i) => (
            <div key={i} className={`flex items-start gap-2 rounded-lg border px-4 py-2 text-sm ${
              a.severity === "warning"
                ? "border-rose-200 bg-rose-50 text-rose-800"
                : "border-sky-200 bg-sky-50 text-sky-800"
            }`}>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/> {a.message}
            </div>
          ))}
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Priorities */}
        <Card className="p-5" data-testid="coach-priorities-panel">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-[#FF5A00]" />
            <h3 className="font-heading text-lg font-bold">Today's Priorities</h3>
          </div>
          {snapLoading ? (
            <div className="text-sm text-zinc-500">Loading…</div>
          ) : priorities.length === 0 ? (
            <div className="text-sm text-zinc-500" data-testid="coach-priorities-empty">
              Nothing urgent right now. Your ship is steady.
            </div>
          ) : (
            <ol className="space-y-3">
              {priorities.slice(0, 5).map((p, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`coach-priority-${i}`}>
                  <div className="w-6 h-6 rounded-full bg-zinc-900 text-white text-xs flex items-center justify-center shrink-0">{i+1}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{p.reason}</div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-zinc-500 flex-wrap">
                      {p.potential_value != null && <span>Value: {fmt$(p.potential_value)}</span>}
                      {p.action_url && (
                        <a href={p.action_url} className="text-[#FF5A00] hover:underline">Open →</a>
                      )}
                      {priorityIsActionable(p) && (
                        <button
                          onClick={() => setActionInitial({
                            kind: p.type,
                            entity_type: entityTypeFromPriority(p),
                            entity_id: p.entity_id,
                            channel: "email",
                            potential_value: p.potential_value,
                          })}
                          data-testid={`coach-priority-${i}-draft-btn`}
                          className="text-emerald-600 hover:underline"
                        >Draft follow-up →</button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>

        {/* Chat */}
        <div className="lg:col-span-2">
          <Card className="flex flex-col h-[600px]" data-testid="coach-chat-panel">
            <div className="border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#FF5A00]"/>
                <span className="font-medium text-sm">Chat with Coach</span>
              </div>
              <Button size="sm" variant="ghost" onClick={newThread} data-testid="coach-new-thread-btn">
                <Plus className="w-3 h-3 mr-1"/> New
              </Button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-sm text-zinc-500">
                  <p className="mb-3">Ask me anything about your business. I'll ground every answer in your actual LeadForge numbers.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="coach-starter-prompts">
                    {STARTERS.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => send(s.q)}
                        data-testid={`coach-starter-${i}`}
                        className="flex items-center gap-2 text-left px-3 py-2 rounded-lg border border-zinc-200 hover:border-[#FF5A00] hover:bg-orange-50/40 text-sm transition"
                      >
                        <s.icon className="w-4 h-4 text-[#FF5A00]"/> {s.q}
                      </button>
                    ))}
                  </div>
                  {missing.length > 0 && (
                    <div className="mt-4 flex items-start gap-2 text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-2">
                      <Info className="w-3.5 h-3.5 mt-0.5"/>
                      <div>
                        Missing data noted: {missing.slice(0, 3).join(", ")}
                        {missing.length > 3 && ` (+${missing.length - 3} more)`}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {messages.map((m, i) => (
                <MessageBubble key={i} msg={m} />
              ))}
              {sending && (
                <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="coach-typing">
                  <Loader2 className="w-3 h-3 animate-spin"/> Coach is thinking…
                </div>
              )}
            </div>

            <div className="border-t border-zinc-200 p-3 flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask the coach anything about your business…"
                disabled={sending}
                data-testid="coach-chat-input"
              />
              <Button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                data-testid="coach-chat-send"
              >
                <Send className="w-4 h-4"/>
              </Button>
            </div>
          </Card>

          {/* Threads */}
          {threads.length > 0 && (
            <Card className="mt-4 p-4" data-testid="coach-threads-panel">
              <div className="text-xs font-mono-accent uppercase tracking-wider text-zinc-500 mb-2">Recent conversations</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {threads.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => loadThread(t.id)}
                    data-testid={`coach-thread-${t.id}`}
                    className={`w-full text-left px-3 py-2 rounded-md flex items-center justify-between text-sm transition ${
                      activeThreadId === t.id ? "bg-orange-50 border border-[#FF5A00]" : "hover:bg-zinc-50 border border-transparent"
                    }`}
                  >
                    <span className="truncate flex-1">{t.title}</span>
                    <span className="text-xs text-zinc-400 mx-2">{t.message_count} msgs</span>
                    <Trash2
                      onClick={(e) => deleteThread(t.id, e)}
                      className="w-3.5 h-3.5 text-zinc-400 hover:text-rose-600"
                    />
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          onSaved={() => { setShowSettings(false); loadSnapshot(); toast.success("Goals saved"); }}
        />
      )}
      {sourcesKind && (
        <CoachSourcesDrawer kind={sourcesKind} period={period} onClose={() => setSourcesKind(null)}/>
      )}
      {actionInitial && (
        <CoachActionDrawer
          initial={actionInitial}
          onClose={() => setActionInitial(null)}
          onDone={() => { setActionInitial(null); toast.success("Draft closed"); loadSnapshot(); }}
        />
      )}
      {showDigest && (
        <CoachDigestModal onClose={() => setShowDigest(false)}/>
      )}
      {bulkSignal && (
        <CoachBulkDraftDrawer
          signal={bulkSignal}
          onClose={() => setBulkSignal(null)}
          onDone={() => { setBulkSignal(null); loadSnapshot(); }}
        />
      )}
    </div>
  );
}

function priorityIsActionable(p) {
  return ["follow_up_estimate", "follow_up_lead", "collect_overdue"].includes(p.type) && p.entity_id;
}

function entityTypeFromPriority(p) {
  if (p.type === "follow_up_estimate") return "estimate";
  if (p.type === "follow_up_lead") return "lead";
  if (p.type === "collect_overdue") return "invoice";
  return "lead";
}

function Tile({ icon: Icon, label, value, sub, tone, accent, testid, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`bg-white border rounded-xl p-4 ${accent ? "border-orange-200" : "border-zinc-200"} ${clickable ? "cursor-pointer hover:border-[#FF5A00] hover:shadow-sm transition" : ""}`}
      data-testid={testid}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-md bg-orange-50 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-[#FF5A00]"/>
        </div>
        <span className="text-[10px] uppercase font-mono-accent tracking-wider text-zinc-500">{label}</span>
      </div>
      <div className="font-heading text-2xl font-bold" data-testid={`${testid}-value`}>{value}</div>
      {sub != null && (
        <div className={`text-xs mt-1 ${
          tone === "up" ? "text-emerald-600" : tone === "down" ? "text-rose-600" : "text-zinc-500"
        }`}>{sub}</div>
      )}
      {clickable && (
        <div className="text-[10px] mt-2 text-[#FF5A00] uppercase tracking-wider">View sources →</div>
      )}
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  const paragraphs = (msg.content || "").split(/\n\n+/);
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`coach-message-${msg.role}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
        isUser ? "bg-zinc-900 text-white" : "bg-orange-50 border border-orange-100 text-zinc-900"
      }`}>
        {paragraphs.map((p, i) => (
          <p key={i} className={i > 0 ? "mt-2" : ""}>
            {p.split(/\n/).map((line, li) => (
              <span key={li}>{line}<br/></span>
            ))}
          </p>
        ))}
        {msg.sources && (
          <MessageSources sources={msg.sources}/>
        )}
      </div>
    </div>
  );
}

function MessageSources({ sources }) {
  // Emitted in the assistant reply body — the counts are already deterministic
  // from BusinessCoachContext.sources_summary. Each count is a real drill-down.
  const items = [
    { kind: "leads", label: "leads", n: sources.leads_analyzed },
    { kind: "estimates", label: "estimates", n: sources.estimates_analyzed },
    { kind: "jobs", label: "jobs", n: sources.jobs_analyzed },
    { kind: "invoices", label: "invoices", n: sources.invoices_analyzed },
  ];
  // Fire an event to the page-level state via custom event so we don't
  // thread a callback through here. Coach.jsx listens for 'coach-open-sources'.
  const open = (kind) => window.dispatchEvent(new CustomEvent("coach-open-sources", { detail: { kind } }));
  return (
    <div className="mt-3 pt-2 border-t border-orange-200/50 text-[10px] uppercase tracking-wider text-zinc-500 font-mono-accent">
      Based on:
      {" "}
      {items.map((it, i) => (
        <span key={it.kind}>
          {i > 0 && " · "}
          <button
            onClick={() => open(it.kind)}
            data-testid={`coach-message-source-${it.kind}`}
            className="underline decoration-dotted underline-offset-2 hover:text-[#FF5A00]"
          >{it.n} {it.label}</button>
        </span>
      ))}
    </div>
  );
}

function SettingsModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    monthly_revenue_goal: "",
    weekly_revenue_goal: "",
    annual_revenue_goal: "",
    close_rate_goal: "",
    average_ticket_goal: "",
    jobs_goal: "",
    primary_priority: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/business-coach/settings").then(({ data }) => {
      setForm({
        monthly_revenue_goal: data.monthly_revenue_goal ?? "",
        weekly_revenue_goal: data.weekly_revenue_goal ?? "",
        annual_revenue_goal: data.annual_revenue_goal ?? "",
        close_rate_goal: data.close_rate_goal ?? "",
        average_ticket_goal: data.average_ticket_goal ?? "",
        jobs_goal: data.jobs_goal ?? "",
        primary_priority: data.primary_priority ?? "",
      });
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        if (k === "primary_priority") payload[k] = v;
        else if (k === "jobs_goal") payload[k] = parseInt(v, 10);
        else payload[k] = parseFloat(v);
      });
      await api.patch("/business-coach/settings", payload);
      onSaved();
    } catch (e) {
      toast.error("Failed to save goals");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="coach-settings-modal">
      <Card className="w-full max-w-lg p-6">
        <h2 className="font-heading text-2xl font-bold mb-1">Coach Goals</h2>
        <p className="text-sm text-zinc-500 mb-4">Set targets so the coach can measure pace and priorities.</p>
        {loading ? <div>Loading…</div> : (
          <div className="space-y-3">
            <Field label="Monthly revenue goal ($)" val={form.monthly_revenue_goal}
                   onChange={(v) => setForm({...form, monthly_revenue_goal: v})} testid="coach-goal-monthly"/>
            <Field label="Weekly revenue goal ($)" val={form.weekly_revenue_goal}
                   onChange={(v) => setForm({...form, weekly_revenue_goal: v})} testid="coach-goal-weekly"/>
            <Field label="Annual revenue goal ($)" val={form.annual_revenue_goal}
                   onChange={(v) => setForm({...form, annual_revenue_goal: v})} testid="coach-goal-annual"/>
            <Field label="Estimate close rate goal (%)" val={form.close_rate_goal}
                   onChange={(v) => setForm({...form, close_rate_goal: v})} testid="coach-goal-close-rate"/>
            <Field label="Average ticket goal ($)" val={form.average_ticket_goal}
                   onChange={(v) => setForm({...form, average_ticket_goal: v})} testid="coach-goal-ticket"/>
            <Field label="Monthly jobs goal (#)" val={form.jobs_goal}
                   onChange={(v) => setForm({...form, jobs_goal: v})} testid="coach-goal-jobs"/>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Primary priority</Label>
              <select
                value={form.primary_priority}
                onChange={(e) => setForm({...form, primary_priority: e.target.value})}
                data-testid="coach-goal-priority"
                className="w-full mt-1 border border-zinc-200 rounded-md px-3 py-2 text-sm"
              >
                <option value="">None</option>
                <option value="growth">Growth</option>
                <option value="profit">Profit</option>
                <option value="revenue">Revenue</option>
                <option value="lead_conversion">Lead conversion</option>
                <option value="cash_flow">Cash flow</option>
                <option value="capacity">Capacity</option>
                <option value="retention">Customer retention</option>
              </select>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} data-testid="coach-goals-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || loading}
                  className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="coach-goals-save">
            {saving ? "Saving…" : "Save goals"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, val, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-500">{label}</Label>
      <Input type="number" step="any" value={val} onChange={(e) => onChange(e.target.value)}
             data-testid={testid} className="mt-1"/>
    </div>
  );
}
